import { FC, FormEvent, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { CreatePayoutProps } from "./create-payout.types";
import { useUIStore } from "@/stores";
import {
  useGetAffiliatePaymentPeriods,
  usePayAffiliatePayment,
} from "@/features/affiliate-payouts/queries";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { useAffiliatePaymentSum } from "@/features/affiliate-payouts/queries/get-affiliate-payment-sum.query.ts";
import { PayAffiliatePaymentRequest } from "@/api/affiliates/types";

const CreatePayout: FC<CreatePayoutProps> = ({ affiliateId }) => {
  const { closeModal } = useUIStore();
  const { paymentPeriods, isPaymentPeriodsLoading } =
    useGetAffiliatePaymentPeriods(affiliateId);

  const [selectedPeriod, setSelectedPeriod] = useState<Dayjs | null>(null);
  const [paymentDate, setPaymentDate] = useState<Dayjs | null>(dayjs());

  const { paymentAmount, isLoadingAmount } = useAffiliatePaymentSum(
    affiliateId,
    selectedPeriod?.year(),
    selectedPeriod ? selectedPeriod.month() + 1 : undefined,
  );

  const { payAffiliatePayment, isProcessingAffiliatePayment } =
    usePayAffiliatePayment();

  const unpaidPeriodsData = useMemo(() => {
    const unpaidPeriods = paymentPeriods.unpaidPeriods;

    const monthSet = new Set(
      unpaidPeriods.map((p) => `${p.year}-${String(p.month).padStart(2, "0")}`),
    );

    const yearSet = new Set(unpaidPeriods.map((p) => p.year));

    return {
      monthSet,
      yearSet,
    };
  }, [paymentPeriods.unpaidPeriods]);

  const onChangePaymentPeriod = (newValue: PickerValue) => {
    if (!newValue) return;

    const newFormattedMonth = newValue.format("YYYY-MM");

    if (unpaidPeriodsData.monthSet.has(newFormattedMonth)) {
      setSelectedPeriod(newValue);
    } else {
      const unpaidMonthsInYear = paymentPeriods.unpaidPeriods.filter(
        (p) => p.year === newValue.year(),
      );

      if (unpaidMonthsInYear.length > 0) {
        const firstMonth = String(unpaidMonthsInYear[0].month).padStart(2, "0");
        const dateToSet = dayjs(`${newValue.year()}-${firstMonth}`);
        setSelectedPeriod(dateToSet);
      }
    }
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedPeriod || !paymentDate) {
      return;
    }
    const body: PayAffiliatePaymentRequest = {
      affiliateId,
      year: selectedPeriod.year(),
      month: selectedPeriod.month() + 1,
      paymentDate: paymentDate.toISOString(),
    };
    payAffiliatePayment(body);
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      p={3}
      bgcolor="#121212"
      borderRadius={2}
    >
      <Stack spacing={2}>
        <DatePicker
          label="Payout Period"
          disabled={
            isPaymentPeriodsLoading ||
            isProcessingAffiliatePayment ||
            paymentPeriods.unpaidPeriods.length === 0
          }
          slots={{
            openPickerIcon: isPaymentPeriodsLoading
              ? () => <CircularProgress size={16} />
              : undefined,
          }}
          views={["year", "month"]}
          value={selectedPeriod}
          onChange={onChangePaymentPeriod}
          shouldDisableYear={(date) =>
            !unpaidPeriodsData.yearSet.has(date.year())
          }
          minDate={dayjs("2025-01-01")}
          maxDate={dayjs("2030-01-01")}
          shouldDisableMonth={(date) =>
            !unpaidPeriodsData.monthSet.has(date.format("YYYY-MM"))
          }
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                bgcolor: "#1E1E1E",
                input: { color: "#fff" },
                label: { color: "#ccc" },
              },
            },
          }}
        />

        <DatePicker
          label="Date of Payment"
          value={paymentDate}
          disabled={isProcessingAffiliatePayment}
          onChange={(newValue) => setPaymentDate(newValue)}
          disableFuture
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                bgcolor: "#1E1E1E",
                input: { color: "#fff" },
                label: { color: "#ccc" },
              },
            },
          }}
        />

        <TextField
          label="Amount"
          value={paymentAmount || ""}
          InputProps={{
            readOnly: true,
            endAdornment: isLoadingAmount ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
          fullWidth
          sx={{
            bgcolor: "#1E1E1E",
            input: { color: "#fff" },
            label: { color: "#ccc" },
          }}
        />

        <Stack direction="row" spacing={2} mt={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={closeModal}
            sx={{ bgcolor: "#fff", color: "#000" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            loading={isProcessingAffiliatePayment}
            disabled={
              !selectedPeriod || !paymentDate || isProcessingAffiliatePayment
            }
          >
            Create Payout
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export { CreatePayout };
